
import { Component, computed, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterModule, Routes } from '@angular/router';
import { loginRoutes } from '@softapps/allgemein/login';
import { moduleRoutes } from '@softapps/allgemein/modules';
import { passwordRoutes } from '@softapps/allgemein/password';
import { settingsRoutes } from '@softapps/allgemein/utils';
import { artikelvorratRoutes } from '@softapps/mde/artikelvorrat';
import { bestellerfassungRoutes } from '@softapps/mde/bestellerfassung';
import { bestellvorschlagRoutes } from '@softapps/mde/bestellvorschlag';
import { etikettendruckRoutes } from '@softapps/mde/etikettendruck';
import { lagerInventurRoutes } from '@softapps/mde/inventur';
import { retourenRoutes } from '@softapps/mde/retouren';
import { storageLocationRoutes } from '@softapps/mde/storage-location';
import { warenverprobungRoutes } from '@softapps/mde/warenverprobung';
import { pmsStempelTerminalRoutes } from '@softapps/pms/stempel-terminal';
import { orderRoutes } from '@softapps/werkstatt/order';
import { artikelInfoRoutes } from '@softapps/wws/artikel-info';
import { energietransportRoutes } from '@softapps/wws/energietransport';
import { lieferscheinRoutes } from '@softapps/wws/lieferschein';
import { showRequestErrors } from '@softline/application';
import {
  AuthenticationGuard,
  JwtAuthenticationStore,
  LogoutService,
  SOFTLINE_FEATURE_JWT_AUTHENTICATION,
  UserInfoService,
} from '@softline/auth';
import { ConnectionError, Store, UnauthorizedError } from '@softline/core';
import { MessageBarService } from '@softline/ui-core';
import {
  delay,
  distinctUntilChanged,
  interval,
  map,
  of,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { gesamtabfrageRoutes } from '@softapps/wws/gesamtabfrage';
import { dashboardRoutes } from '@softapps/allgemein/dashboard';
import { salesStatisticsRoutes } from '@softapps/wws/sales-statistics';
import { stempelerfassungRoutes } from '@softapps/pms/stempelerfassung';

@Component({
  selector: 'soft-terminal-auto-login',
  template: '<router-outlet></router-outlet>',
  styles: [],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterModule],
})
export class TerminalAutoLoginComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly messageBarService = inject(MessageBarService);
  private readonly logoutService = inject(LogoutService);
  private readonly userInfo = inject(UserInfoService);
  private readonly userName$ = toObservable(computed(() => this.userInfo.value()?.userName));

  loading = signal(false);

  private subscription?: Subscription;

  async ngOnInit(): Promise<void> {
    this.subscription = this.userName$
      .pipe(
        distinctUntilChanged(),
        switchMap((userName) => {
          if (userName) {
            return of(null);
          }

          const user = {
            username: 'terminal',
            password: '!?Terminal*',
          };

          return interval(5000).pipe(
            startWith(0),
            map(() => user),
            delay(100)
          );
        })
      )
      .subscribe(async (o) => {
        if (!o) return;

        console.debug(
          '[TerminalAutoLoginComponent]: Logout occurred -> automatic login with user: ',
          o?.username
        );

        try {
          this.loading.set(true);

          await this.store.dispatch(
            SOFTLINE_FEATURE_JWT_AUTHENTICATION,
            JwtAuthenticationStore.actions.authenticate,
            { ...o, stayAuthenticated: true }
          );
        } catch (e) {
          if (e instanceof UnauthorizedError) {
            console.debug(
              '[TerminalAutoLoginComponent]: Auto Login failed due to being unauthorized',
              e
            );
            this.messageBarService.warning('#LOGIN.MESSAGES.FAILED.UNAUTHORIZED');
          } else if (e instanceof ConnectionError) {
            console.debug(
              '[TerminalAutoLoginComponent]: Auto Login failed due to a bad connection',
              e
            );
            this.messageBarService.warning('#LOGIN.MESSAGES.FAILED.NO_CONNECTION');
          } else {
            console.debug('[TerminalAutoLoginComponent]: Auto Login failed due to:', e);
            showRequestErrors(this.store, e);
          }
        } finally {
          this.loading.set(false);
        }
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}

export const routes: Routes = [
  {
    path: 'terminal',
    loadComponent: () => import('@softapps/core').then((o) => o.TerminalComponent),
    loadChildren: () => [
      {
        // TODO: Remove when authentication via access token works -> this is an auto login workaround...
        path: '',
        component: TerminalAutoLoginComponent,
        children: [...pmsStempelTerminalRoutes],
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('@softapps/core').then((o) => o.ShellComponent),
    children: [
      //Allgemein
      ...moduleRoutes,
      ...settingsRoutes,

      // MDE
      ...bestellvorschlagRoutes,
      ...bestellerfassungRoutes,
      ...etikettendruckRoutes,
      ...lagerInventurRoutes,
      ...retourenRoutes,
      ...warenverprobungRoutes,
      ...artikelInfoRoutes,
      ...artikelvorratRoutes,
      ...storageLocationRoutes,

      ...gesamtabfrageRoutes,
      ...dashboardRoutes,
      ...salesStatisticsRoutes,

      ...energietransportRoutes,
      ...lieferscheinRoutes,
      ...orderRoutes,
      ...pmsStempelTerminalRoutes,
      ...stempelerfassungRoutes,
    ],
    canActivate: [AuthenticationGuard],
  },
  {
    path: '',
    loadComponent: () => import('@softapps/core').then((o) => o.PublicShellComponent),
    children: [...loginRoutes, ...passwordRoutes, ...settingsRoutes],
  },
];
